import { jsx, jsxs } from 'react/jsx-runtime';
import { Tooltip } from '@ubnt/ui-components/Tooltip';
import { useState, useMemo, useCallback, Fragment } from 'react';
import { useWindowSize } from 'react-use';
import { CONTAINER_WIDTH } from '../../../../constants/layout.mjs';
import '../../../../constants/modelCompareData/assets/tooltip-info.svg.mjs';
import { emptyColumn } from '../../../../constants/modelCompareData/data/products/emptyColumn.mjs';
import { useModelCompareTranslation } from '../../../../constants/modelCompareData/translations/useModelCompareTranslation.mjs';
import '../../../../contexts/MenuContext/index.mjs';
import { LanguageDirectionType } from '../../../../contexts/MenuContext/utils.mjs';
import '../../../StyleableSvgImage/index.mjs';
import { MOBILE_BREAKPOINT, PlaceholderGridItem, PropertyTitleGridItem, PropertyTitleGridItemContent, PropertyIcon, PropertyTitle, PropertyTooltip, TooltipStyles, PropertyTooltipHoverArea, TooltipIcon, PropertyTooltipContent, TOOLTIP_CLASS_NAME, EmptyPropertyTitleGridItem } from '../CompareTable.styles.mjs';
import { getNumberOfColumns } from '../helpers/getNumberOfColumns.mjs';
import { useButtonRenderer } from '../helpers/RenderButton.mjs';
import { ModelCompareOverflowScrollButton } from './components/CompareTableOverflowScrollButton.mjs';
import { EmptyColumnHeader } from './components/EmptyColumnHeader/EmptyColumnHeader.mjs';
import { FootNotes } from './components/FootNotes/FootNotes.mjs';
import { HeaderShadow } from './components/HeaderShadow.mjs';
import { Notes } from './components/Notes/Notes.mjs';
import { ProductHeader } from './components/ProductHeader/ProductHeader.mjs';
import { PropertyValueGridItem } from './components/PropertyValueGridItem.mjs';
import './components/StickyGrid/index.mjs';
import { useMenu } from '../../../../contexts/MenuContext/context.mjs';
import { StickyGrid } from './components/StickyGrid/StickyGrid.mjs';
import { StickyGridHeader } from './components/StickyGrid/StickyGridHeader.mjs';
import { StickyGridBody } from './components/StickyGrid/StickyGridBody.mjs';
import { StyleableSvgImage } from '../../../StyleableSvgImage/StyleableSvgImage.mjs';
import tooltipIcon__default from "../../../../assets/tooltip-info.svg";
import { StickyGridOverlay } from './components/StickyGrid/StickyGridOverlay.mjs';
"use strict";
const SMOOTH_SIZE_REDUCTION = 4;
const CompareTableMarkup = ({
  products,
  properties,
  columnWidth = 338,
  propertyColumnWidth = 230,
  headerStickOffset,
  overlayTopOffset,
  showBuyButton,
  onLinkClick,
  linkToStoreTitleClick,
  showBadges,
  setSelectedProducts,
  hasBeenObserved,
  selectedProductsLimit,
  setExternalState,
  selectedProducts,
  currAvailableProducts,
  isOverviewPage,
  isStaticTable
}) => {
  const {
    storeId
  } = useMenu();
  const {
    formatMessage
  } = useModelCompareTranslation();
  const renderButton = useButtonRenderer();
  const {
    width: screenWidth
  } = useWindowSize();
  const isLessThanOrEqualToFiveProducts = currAvailableProducts.length <= 5;
  const [headerItemsAreSticking, setHeaderItemsAreSticking] = useState(false);
  const isSmallScreen = useMemo(() => screenWidth < MOBILE_BREAKPOINT, [screenWidth]);
  const isGreaterThanDesktopWidth = useMemo(() => screenWidth > CONTAINER_WIDTH.DESKTOP, [screenWidth]);
  const leftColumnWidth = useMemo(() => isSmallScreen ? 0 : Math.min(screenWidth / SMOOTH_SIZE_REDUCTION, propertyColumnWidth), [isSmallScreen, propertyColumnWidth, screenWidth]);
  const maxImageHeight = useMemo(() => Math.max(...products.map(({
    image
  }) => image.height)), [products]);
  const stickOffset = useMemo(() => headerStickOffset ? headerStickOffset({
    maxImageHeight
  }) : 0, [headerStickOffset, maxImageHeight]);
  const numberOfColumns = useMemo(() => isStaticTable ? products.length : getNumberOfColumns({
    selectedProducts,
    selectedProductsLimit,
    products
  }), [selectedProducts, selectedProductsLimit, products, isStaticTable]);
  const templateColumnWidth = useMemo(() => {
    if (isSmallScreen) return "100%";
    if (isGreaterThanDesktopWidth) return "1fr";
    return `${columnWidth}px`;
  }, [isSmallScreen, columnWidth, isGreaterThanDesktopWidth]);
  const templateColumns = useMemo(() => `${leftColumnWidth}px repeat(${numberOfColumns}, ${templateColumnWidth})`, [leftColumnWidth, numberOfColumns, templateColumnWidth]);
  const hasNotes = useMemo(() => {
    return products.some(({
      notes
    }) => !!notes?.length);
  }, [products]);
  const hasFootnotes = useMemo(() => {
    return products.some(({
      footnotes
    }) => footnotes?.length);
  }, [products]);
  const getComingSoonButtonText = useCallback(product => {
    const buttonText = product.comingSoonButtonText;
    if (typeof buttonText === "string") {
      return formatMessage(buttonText);
    }
    if (typeof buttonText === "function" && !!buttonText(storeId)) {
      const foundKey = buttonText(storeId);
      if (foundKey) {
        return formatMessage(foundKey);
      }
    }
    return formatMessage("Coming Soon");
  }, [formatMessage, storeId]);
  const hasMultipleProductsSelected = useMemo(() => products.filter(({
    id
  }) => id !== emptyColumn.id).length > 1, [products]);
  return (
    // APPLY left to right override in all cases rather than redo the model compare for Arabic
    /* @__PURE__ */
    jsx("section", {
      dir: LanguageDirectionType.ltr,
      children: /* @__PURE__ */jsxs(StickyGrid, {
        templateColumns,
        children: [/* @__PURE__ */jsxs(StickyGridHeader, {
          stickOffset,
          onStickyStateChange: setHeaderItemsAreSticking,
          nonScrollChildren: ({
            grid: {
              isAtMinX
            },
            isStuck
          }) => {
            return /* @__PURE__ */jsx(HeaderShadow, {
              leftColumnWidth,
              shadowLeftColumn: isAtMinX,
              show: isStuck
            });
          },
          children: [/* @__PURE__ */jsx(PlaceholderGridItem, {
            stickX: true
          }), products.map((product, index) => product.propertyValues.length ? /* @__PURE__ */jsx(ProductHeader, {
            isStaticProduct: isStaticTable || !isOverviewPage && index === 0,
            product,
            setSelectedProducts,
            index,
            setExternalState,
            selectedProducts,
            linkToStoreTitleClick,
            hasBeenObserved,
            isLessThanOrEqualToFiveProducts,
            hasMultipleProductsSelected,
            isSmallScreen,
            onLinkClick,
            showBadges
          }, `${product.id}-${index}`) : /* @__PURE__ */jsx(EmptyColumnHeader, {
            product,
            index,
            hasBeenObserved,
            maxImageHeight,
            headerItemsAreSticking
          }, `${product.id}-${index}`))]
        }), /* @__PURE__ */jsxs(StickyGridBody, {
          children: [properties.map(property => /* @__PURE__ */jsxs(Fragment, {
            children: [/* @__PURE__ */jsx(PropertyTitleGridItem, {
              stickX: true,
              children: !!leftColumnWidth && /* @__PURE__ */jsxs(PropertyTitleGridItemContent, {
                children: [/* @__PURE__ */jsx(PropertyIcon, {
                  children: /* @__PURE__ */jsx(StyleableSvgImage, {
                    width: 30,
                    height: 30,
                    src: property.icon
                  })
                }), /* @__PURE__ */jsx(PropertyTitle, {
                  children: property.title.split("\n").map((part, i, arr) => /* @__PURE__ */jsxs(Fragment, {
                    children: [part, i < arr.length - 1 && /* @__PURE__ */jsx("br", {})]
                  }, i))
                }), property.tooltip && /* @__PURE__ */jsxs(PropertyTooltip, {
                  children: [/* @__PURE__ */jsx(TooltipStyles, {
                    maxWidth: property.tooltipWidth
                  }), /* @__PURE__ */jsxs(Tooltip, {
                    tooltipClassName: TOOLTIP_CLASS_NAME,
                    description: /* @__PURE__ */jsx(PropertyTooltipContent, {
                      dangerouslySetInnerHTML: {
                        __html: property.tooltip
                      }
                    }),
                    portal: true,
                    motif: "dark",
                    isInteractive: true,
                    position: "right",
                    width: property.tooltipWidth,
                    children: [/* @__PURE__ */jsx(PropertyTooltipHoverArea, {}), /* @__PURE__ */jsx(TooltipIcon, {
                      width: 20,
                      height: 20,
                      src: tooltipIcon__default
                    })]
                  })]
                })]
              })
            }), products.map((product, index) => {
              const isLastProduct = index === products.length - 1;
              const productProperty = product.propertyValues?.find(product2 => product2.propertyId === property.id);
              return /* @__PURE__ */jsx(PropertyValueGridItem, {
                isLastProduct,
                productProperty,
                property,
                includeProperty: isSmallScreen,
                index
              }, `${product.id}-${index}`);
            })]
          }, property.id)), hasNotes && /* @__PURE__ */jsx(Notes, {
            products
          }), /* @__PURE__ */jsx(EmptyPropertyTitleGridItem, {
            stickX: true
          }), products.map((product, index) => renderButton({
            product,
            index,
            getComingSoonButtonText,
            showBuyButton,
            onLinkClick
          })), hasFootnotes && /* @__PURE__ */jsx(FootNotes, {
            products
          })]
        }), /* @__PURE__ */jsx(StickyGridOverlay, {
          style: {
            left: `${leftColumnWidth}px`
          },
          topOffset: overlayTopOffset,
          children: ({
            isAtMinX
          }) => /* @__PURE__ */jsx(ModelCompareOverflowScrollButton, {
            isVisible: !isAtMinX,
            color: leftColumnWidth > 0 ? "rgb(23 49 73 / 0.1)" : "white",
            rotationDegrees: 180,
            onClickColumnDiff: -1,
            totalColumns: products.length,
            totalDeadSpace: leftColumnWidth,
            isSmallScreen
          })
        }), /* @__PURE__ */jsx(StickyGridOverlay, {
          style: {
            right: 0
          },
          topOffset: overlayTopOffset,
          children: ({
            isAtMaxX
          }) => /* @__PURE__ */jsx(ModelCompareOverflowScrollButton, {
            isLeftButton: false,
            isVisible: !isAtMaxX,
            color: "white",
            rotationDegrees: 0,
            onClickColumnDiff: 1,
            totalColumns: products.length,
            totalDeadSpace: leftColumnWidth,
            isSmallScreen
          })
        })]
      })
    })
  );
};
export { CompareTableMarkup };
//# sourceMappingURL=CompareTableMarkup.mjs.map
